/**
 * InsufficientSparksContext
 *
 * Workspace-level event bus that lets node-level Generate buttons
 * trigger the global <InsufficientSparksModal>. Kept separate from
 * CanvasContext (which is large and React-Flow-coupled) so consumers
 * subscribe only to the modal-trigger event without re-rendering on
 * unrelated canvas state changes.
 *
 * Usage:
 *   - WorkspacePageV2 wraps its tree in <InsufficientSparksProvider>
 *     and reads `request` to render the modal.
 *   - Generate-button handlers call useShowInsufficientSparks() and
 *     invoke the returned function from their executeNode catch block.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react'

export interface InsufficientSparksDetail {
  balance: number
  required: number
  /** Optional contextual node id — for diagnostics only; modal copy
   *  doesn't reference it today. */
  nodeId?: string
  /** Optional callback invoked once the user has topped up enough
   *  credits to clear ``required``. Lets UsageCostModal auto-resume
   *  the failed action instead of forcing the user to track it down
   *  on the canvas again. */
  //  Return type is `void`, not a Promise: the result is discarded at the
  //  call site, and TS's void-return rule lets an async callback satisfy it
  //  without naming a payload type nothing reads.
  retry?: () => void
}

interface ContextValue {
  request: InsufficientSparksDetail | null
  show: (detail: InsufficientSparksDetail) => void
  dismiss: () => void
  /** Same as ``dismiss`` but additionally invokes the captured retry
   *  callback (if any). Called by UsageCostModal after a successful
   *  ``addSparks`` mutation when the new balance ≥ required. */
  resolveAndRetry: () => void
}

const Ctx = createContext<ContextValue>({
  request: null,
  show: () => {},
  dismiss: () => {},
  resolveAndRetry: () => {},
})

/** Workspace-side hook — read the current request to drive the modal. */
export function useInsufficientSparks(): ContextValue {
  return useContext(Ctx)
}

/**
 * Component-side hook — get the stable `show` setter without
 * subscribing to `request`. Generate buttons should use this so they
 * don't re-render every time the modal opens/closes.
 */
export function useShowInsufficientSparks(): (detail: InsufficientSparksDetail) => void {
  return useContext(Ctx).show
}

export function InsufficientSparksProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [request, setRequest] = useState<InsufficientSparksDetail | null>(null)

  const show = useCallback((detail: InsufficientSparksDetail) => {
    setRequest(detail)
  }, [])
  const dismiss = useCallback(() => setRequest(null), [])

  const resolveAndRetry = useCallback(() => {
    setRequest((current) => {
      // Defer the retry so the request is cleared before the action
      // re-fires — otherwise the same modal might re-open instantly if
      // the retry trips another 402.
      const retry = current?.retry
      if (retry) {
        // Fire-and-forget; callers that need error handling do their
        // own try/catch inside the retry closure.
        Promise.resolve().then(() => retry()).catch(() => {
          /* swallowed — the retry path owns its own error UX */
        })
      }
      return null
    })
  }, [])

  const value = useMemo<ContextValue>(
    () => ({ request, show, dismiss, resolveAndRetry }),
    [request, show, dismiss, resolveAndRetry],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
